import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  Title,
  Metric,
  Grid,
} from '@tremor/react';
import {
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  LabelList,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import DatePicker from './DatePicker';
import './KPIStatPage.css';

const PROJECT = 'MAN';
const COL_REP_PICK = 'REP PICK';
const COL_SHIP_REP = 'SHIP REP';

const STORAGE_KEY_DATE_FROM = 'oww-dateFrom';
const STORAGE_KEY_DATE_TO = 'oww-dateTo';

function getInitialDateFrom() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_FROM);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDateForInput(d);
}

function getInitialDateTo() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_TO);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  return formatDateForInput(new Date());
}

const ZMIANA_OPTIONS = [
  { key: 'both', label: 'A i B' },
  { key: 'A', label: 'A' },
  { key: 'B', label: 'B' },
];

const CHART_COLORS = { A: '#00bcd4', B: '#ff9800', maA: '#4caf50', maB: '#f44336' };

const TREND_LEGEND_ITEMS = [
  { key: 'A', label: 'A', color: CHART_COLORS.A },
  { key: 'B', label: 'B', color: CHART_COLORS.B },
  { key: 'maA', label: 'Śr. 7D (A)', color: CHART_COLORS.maA },
  { key: 'maB', label: 'Śr. 7D (B)', color: CHART_COLORS.maB },
];

function TrendChartLegend({ visible, onToggle }) {
  return (
    <div className="recharts-default-legend" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 8 }}>
      {TREND_LEGEND_ITEMS.map(({ key, label, color }) => (
        <span
          key={key}
          role="button"
          tabIndex={0}
          onClick={() => onToggle(key)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(key); } }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: visible[key] ? 1 : 0.45, userSelect: 'none' }}
        >
          <span style={{ width: 14, height: 14, borderRadius: 2, background: color }} />
          <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
        </span>
      ))}
    </div>
  );
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateOnly(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  return headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function dateToStr(d) {
  return d ? formatDateForInput(d) : '';
}

function getStartOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function filterRowsByDateRange(rows, headers, rangeFrom, rangeTo) {
  if (!rows.length || !rangeFrom || !rangeTo) return rows;
  const col = getColumnIndex(headers, 'DATA');
  if (col < 0) return rows;
  const from = new Date(rangeFrom);
  const to = new Date(rangeTo);
  to.setHours(23, 59, 59, 999);
  return rows.filter((row) => {
    const val = row[col] && typeof row[col] === 'object' ? row[col].value : row[col];
    const d = parseDateOnly(val);
    if (!d) return false;
    return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
  });
}

function filterRowsByZmiana(rows, headers, zmianaFilter) {
  if (zmianaFilter === 'both' || !rows.length) return rows;
  const col = getColumnIndex(headers, 'ZMIANA');
  if (col < 0) return rows;
  return rows.filter((row) => {
    const val = row[col] && typeof row[col] === 'object' ? row[col].value : row[col];
    return String(val ?? '').trim().toUpperCase() === zmianaFilter;
  });
}

/** byDay[dateStr] = { repPickA, repPickB, shipRepA, shipRepB } */
function aggregateByDayAndZmiana(rows, headers) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  const repPickCol = getColumnIndex(headers, COL_REP_PICK);
  const shipRepCol = getColumnIndex(headers, COL_SHIP_REP);
  if (dataCol < 0 || repPickCol < 0 || shipRepCol < 0) return {};
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    const z = zmianaCol >= 0
      ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase()
      : '';
    const zm = z === 'A' || z === 'B' ? z : 'A';
    if (!byDay[dateStr]) byDay[dateStr] = { repPickA: 0, repPickB: 0, shipRepA: 0, shipRepB: 0 };
    byDay[dateStr][`repPick${zm}`] += parseNumber(row[repPickCol]);
    byDay[dateStr][`shipRep${zm}`] += parseNumber(row[shipRepCol]);
  });
  return byDay;
}

function computeKpi(byDay, rangeFrom, rangeTo) {
  const dayRepPick = (dateStr) => {
    const o = byDay[dateStr] || { repPickA: 0, repPickB: 0 };
    return o.repPickA + o.repPickB;
  };
  const dayShipRep = (dateStr) => {
    const o = byDay[dateStr] || { shipRepA: 0, shipRepB: 0 };
    return o.shipRepA + o.shipRepB;
  };
  const sortedDates = Object.keys(byDay).sort();
  const lastDayStr = sortedDates.length ? sortedDates[sortedDates.length - 1] : null;
  const prevDayStr = sortedDates.length > 1 ? sortedDates[sortedDates.length - 2] : null;
  const lastDaySum = lastDayStr ? dayRepPick(lastDayStr) : 0;
  const prevDaySum = prevDayStr ? dayRepPick(prevDayStr) : 0;
  const dodPct = prevDaySum !== 0 ? (((lastDaySum - prevDaySum) / prevDaySum) * 100).toFixed(1) : (lastDaySum > 0 ? '100' : '0');
  const changeAmount = prevDayStr != null ? lastDaySum - prevDaySum : null;
  const last7 = sortedDates.slice(-7);
  const avg7d = last7.length ? (last7.reduce((s, d) => s + dayRepPick(d), 0) / last7.length).toFixed(1) : '—';
  const rangeEnd = rangeTo ? new Date(rangeTo) : new Date();
  const rangeStart = rangeFrom ? new Date(rangeFrom) : new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);
  const mtdStart = getStartOfMonth(rangeEnd);
  const mtdFrom = rangeStart.getTime() > mtdStart.getTime() ? rangeStart : mtdStart;
  let mtdSum = 0;
  for (let d = new Date(mtdFrom); d <= rangeEnd; d.setDate(d.getDate() + 1)) mtdSum += dayRepPick(dateToStr(d));
  const totalRepPickA = sortedDates.reduce((s, d) => s + (byDay[d] || {}).repPickA, 0);
  const totalRepPickB = sortedDates.reduce((s, d) => s + (byDay[d] || {}).repPickB, 0);
  const totalRepPick = totalRepPickA + totalRepPickB;
  const totalShipRep = sortedDates.reduce((s, d) => s + dayShipRep(d), 0);
  const aPct = totalRepPick > 0 ? ((totalRepPickA / totalRepPick) * 100).toFixed(1) : '0';
  const bPct = totalRepPick > 0 ? ((totalRepPickB / totalRepPick) * 100).toFixed(1) : '0';
  const ratio = totalShipRep > 0 ? (totalRepPick / totalShipRep).toFixed(2) : '—';
  return {
    lastDaySum, lastDayStr, prevDaySum, dodPct, changeAmount, avg7d, mtdSum, sortedDates, dayRepPick, dayShipRep,
    totalRepPickA, totalRepPickB, totalRepPick, totalShipRep, aPct, bPct, ratio, byDay,
  };
}

function buildLineChartData(byDay) {
  const sortedDates = Object.keys(byDay).sort();
  const data = sortedDates.map((dateStr) => {
    const o = byDay[dateStr] || { repPickA: 0, repPickB: 0 };
    return { dateStr, A: o.repPickA, B: o.repPickB, total: o.repPickA + o.repPickB };
  });
  for (let i = 0; i < data.length; i++) {
    const from = Math.max(0, i - 6);
    const slice = data.slice(from, i + 1);
    data[i].maA = slice.length ? (slice.reduce((s, x) => s + x.A, 0) / slice.length).toFixed(2) : null;
    data[i].maB = slice.length ? (slice.reduce((s, x) => s + x.B, 0) / slice.length).toFixed(2) : null;
  }
  return data;
}

function buildGroupedBarData(byDay) {
  return Object.keys(byDay)
    .sort()
    .map((dateStr) => {
      const o = byDay[dateStr] || {};
      return { dateStr, A: o.repPickA || 0, B: o.repPickB || 0 };
    });
}

/** Ratio per day (total REP PICK / total SHIP REP for that day). */
function buildRatioChartData(byDay) {
  return Object.keys(byDay)
    .sort()
    .map((dateStr) => {
      const o = byDay[dateStr] || {};
      const repPick = o.repPickA + o.repPickB;
      const shipRep = o.shipRepA + o.shipRepB;
      const ratio = shipRep > 0 ? Number((repPick / shipRep).toFixed(2)) : null;
      return { dateStr, ratio, repPick, shipRep };
    })
    .filter((r) => r.ratio != null);
}

function ForklifOww() {
  const [raportSheet, setRaportSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(getInitialDateFrom);
  const [dateTo, setDateTo] = useState(getInitialDateTo);
  const [zmianaFilter, setZmianaFilter] = useState('both');
  const [trendSeriesVisible, setTrendSeriesVisible] = useState({ A: true, B: true, maA: true, maB: true });

  const handleTrendLegendClick = useCallback((key) => {
    setTrendSeriesVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (Object.values(next).filter(Boolean).length === 0) return prev;
      return next;
    });
  }, []);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const raport = list.find((s) => String(s.name || '').trim().toLowerCase() === 'raport');
      setRaportSheet(raport ? { name: raport.name, data: normalizeSheetData(raport.data) } : null);
      if (!raport) setError('Brak arkusza „Raport” w Uzupełnij raport.');
    } catch (err) {
      console.error('Błąd ładowania OWW KPI:', err);
      setError('Nie udało się załadować danych z arkusza Raport.');
      setRaportSheet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => {
    try {
      if (dateFrom) localStorage.setItem(STORAGE_KEY_DATE_FROM, dateFrom);
      if (dateTo) localStorage.setItem(STORAGE_KEY_DATE_TO, dateTo);
    } catch (e) {}
  }, [dateFrom, dateTo]);

  const headers = raportSheet?.data?.[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const dataRows = raportSheet?.data?.slice(1) ?? [];
  const repPickCol = getColumnIndex(headers, COL_REP_PICK);
  const shipRepCol = getColumnIndex(headers, COL_SHIP_REP);
  const hasColumns = repPickCol >= 0 && shipRepCol >= 0;
  const filteredByDate = filterRowsByDateRange(dataRows, headers, dateFrom, dateTo);
  const filteredRows = filterRowsByZmiana(filteredByDate, headers, zmianaFilter);
  const byDay = hasColumns ? aggregateByDayAndZmiana(filteredRows, headers) : {};
  const kpi = hasColumns && Object.keys(byDay).length ? computeKpi(byDay, dateFrom, dateTo) : null;
  const lineData = buildLineChartData(byDay);
  const groupedBarData = buildGroupedBarData(byDay);
  const ratioChartData = buildRatioChartData(byDay);

  return (
    <div className="kpi-stat-page">
      <div className="kpi-ps-header-bar">
        <Title className="kpi-ps-header-title">OWW KPI</Title>
        <div className="kpi-ps-zakres">
          <span className="kpi-ps-zakres-title">Zakres dat</span>
          <div className="kpi-ps-zakres-inner">
            <DatePicker label="Od:" value={dateFrom} onChange={(v) => setDateFrom(v || '')} max={dateTo || undefined} placeholder="Od" inline align="left" className="kpi-ps-zakres-date" />
            <DatePicker label="Do:" value={dateTo} onChange={(v) => setDateTo(v || '')} min={dateFrom || undefined} placeholder="Do" inline align="right" className="kpi-ps-zakres-date" />
          </div>
          <span className="kpi-ps-zakres-title" style={{ marginLeft: 8 }}>ZMIANA</span>
          <div className="kpi-ps-zakres-buttons" role="group">
            {ZMIANA_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`kpi-ps-zakres-btn ${zmianaFilter === o.key ? 'kpi-ps-zakres-btn-active' : ''}`}
                onClick={() => setZmianaFilter(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <Text className="kpi-ps-table-error">{error}</Text>}
      {loading ? (
        <Text className="kpi-ps-table-loading">Ładowanie…</Text>
      ) : !raportSheet ? (
        <Text className="kpi-ps-table-empty">Brak arkusza „Raport” w Uzupełnij raport.</Text>
      ) : !hasColumns ? (
        <Text className="kpi-ps-table-empty">Brak kolumn „{COL_REP_PICK}” lub „{COL_SHIP_REP}” w arkuszu Raport.</Text>
      ) : (
        <>
          <Card className="kpi-ps-shell" style={{ marginBottom: 16 }}>
            <Grid numItemsSm={2} numItemsMd={3} numItemsLg={6} className="kpi-ps-cards">
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">Ostatni dzień{kpi?.lastDayStr ? ` (${kpi.lastDayStr})` : ''}</Text>
                <Metric className="kpi-ps-kpi-metric">{kpi?.lastDaySum ?? '—'}</Metric>
              </Card>
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">% zmiana (ostatni vs poprzedni)</Text>
                <Metric className="kpi-ps-kpi-metric">{kpi?.dodPct ?? '—'}%{kpi?.changeAmount != null ? ` (${kpi.changeAmount >= 0 ? '+' : ''}${kpi.changeAmount})` : ''}</Metric>
              </Card>
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">Średnia 7 dni</Text>
                <Metric className="kpi-ps-kpi-metric">{kpi?.avg7d ?? '—'}</Metric>
              </Card>
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">Od początku miesiąca do dziś.</Text>
                <Metric className="kpi-ps-kpi-metric">{kpi?.mtdSum ?? '—'}</Metric>
              </Card>
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">A% vs B% (udział zmiany)</Text>
                <Metric className="kpi-ps-kpi-metric">A: {kpi?.aPct ?? '—'}% | B: {kpi?.bPct ?? '—'}%</Metric>
              </Card>
              <Card className="kpi-ps-kpi-card">
                <Text className="kpi-ps-kpi-label">Ratio REP PICK / SHIP REP (Wybrany okres)</Text>
                <Metric className="kpi-ps-kpi-metric">{kpi?.ratio ?? '—'}</Metric>
              </Card>
            </Grid>
          </Card>

          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title" style={{ marginBottom: 8 }}>Średnia A vs B + średnia krocząca 7D - Replenishment</Text>
            {lineData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={lineData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Legend content={<TrendChartLegend visible={trendSeriesVisible} onToggle={handleTrendLegendClick} />} />
                  <Line type="monotone" dataKey="A" stroke={CHART_COLORS.A} strokeWidth={trendSeriesVisible.A ? 2 : 0} strokeOpacity={trendSeriesVisible.A ? 1 : 0} name="A" dot={false} hide={!trendSeriesVisible.A} />
                  <Line type="monotone" dataKey="B" stroke={CHART_COLORS.B} strokeWidth={trendSeriesVisible.B ? 2 : 0} strokeOpacity={trendSeriesVisible.B ? 1 : 0} name="B" dot={false} hide={!trendSeriesVisible.B} />
                  <Line type="monotone" dataKey="maA" stroke={CHART_COLORS.maA} strokeWidth={trendSeriesVisible.maA ? 1.5 : 0} strokeOpacity={trendSeriesVisible.maA ? 1 : 0} strokeDasharray="4 4" name="Śr. 7D (A)" dot={false} hide={!trendSeriesVisible.maA} />
                  <Line type="monotone" dataKey="maB" stroke={CHART_COLORS.maB} strokeWidth={trendSeriesVisible.maB ? 1.5 : 0} strokeOpacity={trendSeriesVisible.maB ? 1 : 0} strokeDasharray="4 4" name="Śr. 7D (B)" dot={false} hide={!trendSeriesVisible.maB} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title" style={{ marginBottom: 8 }}>Ilość wydanych pojemników na dzień (A i B) - Replenishment</Text>
            {groupedBarData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={groupedBarData} margin={{ top: 28, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="A" fill={CHART_COLORS.A} name="A" barSize={24} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="A" position="top" fill="#f1f5f9" fontSize={13} style={{ fontWeight: 600 }} formatter={(v) => (v != null && v !== '' ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                  <Bar dataKey="B" fill={CHART_COLORS.B} name="B" barSize={24} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="B" position="top" fill="#f1f5f9" fontSize={13} style={{ fontWeight: 600 }} formatter={(v) => (v != null && v !== '' ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title" style={{ marginBottom: 8 }}>Średnia wydanych pojemników na 1 Trailer - Replenishment</Text>
            {ratioChartData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych (dni z SHIP REP &gt; 0).</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={ratioChartData} margin={{ top: 28, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} label={{ value: 'Ratio', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Bar dataKey="ratio" name="REP PICK / SHIP REP" fill="#64748b" barSize={28} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="ratio" position="top" fill="#f1f5f9" fontSize={12} style={{ fontWeight: 600 }} formatter={(v) => (v != null && v !== '' ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default ForklifOww;
